<script setup lang="ts">
import { useGlobalComponentsStore } from "@/stores/global";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { onMounted, onUnmounted } from 'vue';

const globalComponentsStore = useGlobalComponentsStore();
const { getShowConfirmDialog, getConfirmDialogState: confirmDialogData } = storeToRefs(globalComponentsStore);
const { t } = useI18n();
const emit = defineEmits(['confirm']);

const close = () => {
    globalComponentsStore.toggleShowConfirmDialog();
};

onMounted(() => {
    window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown);
});

const onKeydown = (event: KeyboardEvent) => {
    if (!getShowConfirmDialog.value) {
        return;
    }

    if (event.key === 'Escape') {
        close();
    } else if (event.key === 'Enter') {
        confirm();
    }
};

const confirm = () => {
    let returnData = confirmDialogData.value.return.reduce((acc: {
        [x: string]: any;
    }, key: string | number) => {
        acc[key] = confirmDialogData.value[key];
        return acc;
    }, {});

    // If there is only one return key, return only the value
    if (confirmDialogData.value.return.length === 1) {
        returnData = returnData[confirmDialogData.value.return[0]];
    }

    emit('confirm', returnData);
    close();
};
</script>

<template>
    <v-dialog width="auto" v-model="getShowConfirmDialog" class="dialog" content-class="rounded-0" :class="confirmDialogData.class">
        <v-card content-class="rounded-0">
            <v-toolbar color="primary">
                <v-toolbar-title>{{ confirmDialogData.title || '' }}</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <p v-html="confirmDialogData.text"></p>
            </v-card-text>
            <v-card-actions class="buttons">
                <v-spacer></v-spacer>
                <v-btn @click="close" v-if="confirmDialogData.showCloseBtn">
                    <v-icon icon="mdi-cancel"></v-icon>
                    {{ t('General.cancel') || '' }}
                </v-btn>
                <v-btn @click="confirm">
                    <v-icon :icon="confirmDialogData.icon"></v-icon>
                    {{ confirmDialogData.confirmBtnText || '' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss">
@import '@/assets/css/main.scss';

.dialog {
    & header {
        background-color: var(--blue) !important;
    }

    & .buttons button {
        background-color: var(--grey-light);
        color: var(--grey-dark);

        &:last-child {
            color: var(--white);
            background-color: var(--blue-dark);
        }

        & i {
            margin-right: 5px;
        }
    }

    &.danger .buttons button:last-child {
        background-color: var(--color-danger);
    }
}
</style>
